import React, { useCallback, useEffect, useState } from "react";

import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { Box } from "@mui/material";

import axios from "axios";
import { baseURL } from "configurations/environment";
import moment from "moment";

import { RequestConfig } from "helpers/RequestConfig";

import EmptyState from "components/atoms/EmptyState";
import EllipsisText from "components/EllipsisText";
import GenericTable from "components/organisms/GenericTable";
import { GenericTableColumn } from "components/organisms/GenericTable/GenericTable.types";

import {
    buildDevMockEventErrors,
    withDevMockEventErrors,
} from "../../TrackerReports.mock";
import * as Styled from "../../TrackerReports.styled";

enum DateRange {
    empty = "",
    today = "today",
    yesterday = "yesterday",
    last7Days = "last7Days",
    last28Days = "last28Days",
    last3Months = "last3Months",
}

interface EventError {
    event_name: string;
    pixel_id: string;
    message: string | string[] | object;
    title: string;
    created_at: string;
    description: string;
    error_count: number;
}

const formatErrorMessage = (message: string | string[] | object): string => {
    if (typeof message === "string") {
        return message;
    }

    if (Array.isArray(message)) {
        return message.join(", ");
    }

    if (typeof message === "object" && message !== null) {
        try {
            return JSON.stringify(message);
        } catch {
            return "Invalid error message format";
        }
    }

    return "No error message available";
};

const createEventErrorColumns = (): GenericTableColumn<EventError>[] => [
    {
        key: "event_name",
        header: "Event Name",
        label: "Event Name",
        width: 1.2,
        minWidth: 140,
        render: (row) => (
            <EllipsisText text={row.event_name ?? "—"} width="100%" />
        ),
    },
    {
        key: "pixel_id",
        header: "Pixel ID",
        label: "Pixel ID",
        width: 1,
        minWidth: 120,
        render: (row) => (
            <EllipsisText text={String(row.pixel_id ?? "—")} width="100%" />
        ),
    },
    {
        key: "message",
        header: "Error Message",
        label: "Error Message",
        width: 3,
        minWidth: 220,
        render: (row) => (
            <EllipsisText text={formatErrorMessage(row.message)} width="100%" />
        ),
    },
    {
        key: "created_at",
        header: "Error Timestamp",
        label: "Error Timestamp",
        width: 1.2,
        minWidth: 150,
        render: (row) => (
            <EllipsisText
                text={moment(row.created_at).format("YYYY-MM-DD HH:mm")}
                width="100%"
            />
        ),
    },
    {
        key: "error_count",
        header: "Error Count",
        label: "Error Count",
        align: "center",
        fixedWidth: 120,
        render: (row) => (
            <Styled.StatusChip $bg="rgba(220, 38, 38, 0.1)" $color="#DC2626">
                <ErrorOutlineIcon sx={{ fontSize: 13 }} />
                {row.error_count ?? 0}
            </Styled.StatusChip>
        ),
    },
];

const EventErrorTable = ({
    trackerId,
    show,
    startDate,
    endDate,
    selectedFilterDate,
}) => {
    const [errors, setErrors] = useState<EventError[]>([]);

    const applyDateRange = (range) => {
        const today = moment();
        switch (range) {
            case "1":
                return {
                    startDate: today.startOf("day").toDate(),
                    endDate: today.endOf("day").toDate(),
                };

            case "2":
                return {
                    startDate: today
                        .clone()
                        .subtract(1, "day")
                        .startOf("day")
                        .toDate(),
                    endDate: today
                        .clone()
                        .subtract(1, "day")
                        .endOf("day")
                        .toDate(),
                };

            case "7":
                return {
                    startDate: today
                        .clone()
                        .subtract(6, "days")
                        .startOf("day")
                        .toDate(),
                    endDate: today.endOf("day").toDate(),
                };

            case "28":
                return {
                    startDate: today
                        .clone()
                        .subtract(27, "days")
                        .startOf("day")
                        .toDate(),
                    endDate: today.endOf("day").toDate(),
                };

            case "90":
                return {
                    startDate: today
                        .clone()
                        .subtract(90, "days")
                        .startOf("day")
                        .toDate(),
                    endDate: today.endOf("day").toDate(),
                };

            case DateRange.empty:
            default:
                return {
                    startDate: null,
                    endDate: null,
                };
        }
    };

    const fetchErrors = useCallback(async () => {
        let formattedStart;
        let formattedEnd;
        if (startDate && endDate && show === true) {
            formattedStart = moment(startDate).format("YYYY-MM-DD");
            formattedEnd = moment(endDate).format("YYYY-MM-DD");
        } else if (selectedFilterDate) {
            const _getDate = applyDateRange(selectedFilterDate);
            formattedStart = _getDate.startDate;
            formattedEnd = _getDate.endDate;
        }

        try {
            const response = await axios.get(
                `${baseURL}/api/trackers/reports/${trackerId}/event-errors`,
                RequestConfig({
                    params: {
                        start_date: moment(formattedStart).format("YYYY-MM-DD"),
                        end_date: moment(formattedEnd).format("YYYY-MM-DD"),
                    },
                }),
            );
            if (response.data?.status) {
                let parsed;

                const rawData = response?.data?.data;

                if (Array.isArray(rawData)) {
                    parsed = rawData;
                } else if (rawData && typeof rawData === "object") {
                    parsed = Object.values(rawData);
                } else {
                    parsed = []; // or handle as needed
                }

                setErrors(
                    withDevMockEventErrors(
                        parsed,
                        buildDevMockEventErrors,
                    ) as EventError[],
                );
            }
        } catch (err) {
            // console.error("Error fetching event errors", err);
            // In DEV_MODE the fixtures stand in so the table still renders.
            setErrors(
                withDevMockEventErrors(
                    [],
                    buildDevMockEventErrors,
                ) as EventError[],
            );
        }
    }, [endDate, startDate, trackerId, selectedFilterDate, show]);

    useEffect(() => {
        if (startDate && endDate && show === true) {
            fetchErrors();
        }
    }, [startDate, endDate, show, fetchErrors]);
    useEffect(() => {
        selectedFilterDate && fetchErrors();
    }, [selectedFilterDate, fetchErrors]);

    if (!errors.length) return null;

    return (
        <Box mt={3}>
            <Styled.SectionCard elevation={0} $flush>
                <Styled.SectionHeader>
                    <Box>
                        <Styled.SectionTitle>Event Errors</Styled.SectionTitle>
                        <Styled.SectionSubtitle>
                            {`${errors.length} error ${
                                errors.length === 1 ? "group" : "groups"
                            } in the selected range`}
                        </Styled.SectionSubtitle>
                    </Box>
                </Styled.SectionHeader>
                <GenericTable
                    columns={createEventErrorColumns()}
                    rows={errors}
                    getRowKey={(row: EventError, index: number) =>
                        `${row.event_name}-${row.pixel_id}-${index}`
                    }
                    tableName="tracker-report-event-errors"
                    columnToggle
                    pageSize={10}
                    emptyState={
                        <EmptyState
                            icon={<ErrorOutlineIcon />}
                            title="No event errors"
                            description="No destination rejected an event in the selected range."
                            flush
                        />
                    }
                />
            </Styled.SectionCard>
        </Box>
    );
};

export default EventErrorTable;
